import React from 'react';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import { Row, Col } from 'react-styled-flexboxgrid';
import { GetStaticProps } from 'next';

import MainLayout from '../../layouts/MainLayout';
import Section from '@/components/ui/Section';
import ResponsiveGrid from '@/components/ui/ResponsiveGrid';
import PostContent from '@/components/Blog/PostContent';
import PreviewCompatibleImage from '@/components/PreviewCompatibleImage';
import PageHeadline from '@/components/ui/elements/PageHeadline';
import ApiService from '@/lib/api';
import HubspotContactForm from '@/components/HubspotContactForm';
import Link from 'next/link';
import { Namespaces } from '@/types/pages';

const FAQ_SLUG = 'faq';

const AdjustedSection = styled(Section)`
  iframe {
    form {
      ${breakpoint('xs', 'sm')`
        max-width: 370px;
      `};
    }
    margin-top: 3rem;
  }
`;

const BodyCol = styled(Col)`
  > div {
    padding-right: 0.5rem;
    ${breakpoint('lg')`
      padding-right: 2em;
  `}
  }
`;

const HeadQuartersTitle = styled(Col)`
  h2 {
    margin-bottom: 0.25rem;
    font-size: 1.5rem;
    line-height: 1.81rem;
  }
  p {
    margin-bottom: 1rem;
    line-height: 1.18rem;
    font-size: 1rem;
  }
`;

const LocationsRow = styled(Row)`
  margin-bottom: 7rem;
`;

const FaqLine = styled.p`
  font-size: 1rem;
  margin: 0;

  a {
    text-decoration: underline;
  }
`;

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const apiService = new ApiService();
  const [data, menuData, { footer }, aboutRoutes] = await Promise.all([
    apiService.getContactPage(preview),
    apiService.getMenuData(),
    apiService.getMiscData(),
    apiService.getCustomPageRoutes(Namespaces.ABOUT),
  ]);

  // /about/faq had no inbound link anywhere on the site, so it sat in the
  // sitemap as an orphan. Contact is where a visitor is most likely to want it.
  // Gated on the page existing so unpublishing it in Sanity cannot leave a dead
  // link behind (FOX2-173).
  const hasFaqPage = aboutRoutes.some((route) => route.slug?.current === FAQ_SLUG);

  return {
    props: {
      ...data,
      menuData,
      footer,
      hasFaqPage,
    },
    revalidate: 3600,
  };
};

interface ContactProps {
  metaTitle: string;
  metaDescription: string;
  heading: string;
  hubspotPortalId: string;
  hubspotFormId: string;
  address: string;
  body: unknown[];
  headQuarters: {
    location: string;
    image: { image: { alt: string } };
  };
  branchOffices: unknown[];
  menuData: unknown;
  footer: unknown;
  hasFaqPage?: boolean;
}

const ContactPage: React.FC<ContactProps> = (props) => {
  const {
    metaTitle,
    metaDescription,
    heading,
    hubspotPortalId,
    hubspotFormId,
    address,
    body,
    headQuarters,
    branchOffices,
    menuData,
    footer,
    hasFaqPage = false,
  } = props;

  return (
    <MainLayout
      bgWhite={true}
      navIsFixed={false}
      navIsDark={true}
      ogUrl="https://foxbox.com/contact"
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      menuData={menuData}
      footer={footer}
    >
      <AdjustedSection bgColor="white">
        <ResponsiveGrid>
          <Row>
            <BodyCol xs={12} sm={12} md={6} lg={6}>
              <header>
                <PageHeadline xs={32} sm={40} md={48} lg={48}>
                  {heading}
                </PageHeadline>
              </header>
              <PostContent content={body} />

              {hasFaqPage && (
                <FaqLine>
                  Already have a question in mind? <Link href={`/about/${FAQ_SLUG}`}>Read our FAQ</Link>.
                </FaqLine>
              )}

              <HubspotContactForm portalId={hubspotPortalId} formId={hubspotFormId} />
            </BodyCol>
            <Col xs={12} sm={12} md={6} lg={6} className="pr-0 md:pr-8">
              <HeadQuartersTitle>
                <h2>{headQuarters.location}</h2>
                <p>{address}</p>
              </HeadQuartersTitle>
              <PreviewCompatibleImage
                imageInfo={{
                  image: headQuarters.image.image,
                  alt: headQuarters.image.image.alt || headQuarters.location,
                  fadeIn: false,
                  loading: 'eager',
                }}
                style={{ minHeight: '16em' }}
              />
            </Col>
          </Row>

          <LocationsRow>
            {branchOffices.map(({ location, image }, idx) => (
              <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                <h2>{location}</h2>
                <PreviewCompatibleImage
                  imageInfo={{
                    image: image.image,
                    alt: image.alt || location,
                    fadeIn: false,
                    loading: 'eager',
                  }}
                  style={{ minHeight: '8em' }}
                />
              </Col>
            ))}
          </LocationsRow>
        </ResponsiveGrid>
      </AdjustedSection>
    </MainLayout>
  );
};

export default ContactPage;
